import { useParams } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import SEOHead from "@/components/seo/SEOHead";
import { getCommunityBySlug } from "@/data/communityData";
import { IDXDisclaimer } from "@/components/compliance";
import IDXListingsWidget from "@/components/community/IDXListingsWidget";
import IDXListingsGrid from "@/components/community/IDXListingsGrid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin, Home, GraduationCap, Trees, UtensilsCrossed, Train,
  ShoppingBag, Hospital, ArrowRight, Phone, Users, ChevronRight, ExternalLink,
  HelpCircle, BookOpen,
} from "lucide-react";

import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const NotFound = lazy(() => import("./NotFound"));

const ICON_MAP: Record<string, React.ElementType> = {
  school: GraduationCap,
  park: Trees,
  food: UtensilsCrossed,
  transit: Train,
  shopping: ShoppingBag,
  hospital: Hospital,
};

// Towns with custom-generated 1200x630 OG images in /public/images/og/.
// All other communities fall back to the universal /og-image.png.
const TOWNS_WITH_OG_IMAGES = new Set([
  'audubon', 'barrington', 'bellmawr', 'berlin', 'bordentown',
  'burlington-city', 'burlington-township', 'cherry-hill', 'cinnaminson',
  'clementon', 'collingswood', 'delran', 'deptford', 'florence',
  'gloucester-city', 'haddon-heights', 'haddon-township', 'haddonfield',
  'lindenwold', 'lumberton', 'mantua', 'maple-shade', 'marlton',
  'medford-lakes', 'medford', 'merchantville', 'moorestown', 'mount-holly',
  'mount-laurel', 'mullica-hill', 'oaklyn', 'palmyra', 'pennsauken',
  'pine-hill', 'riverside', 'riverton', 'runnemede', 'shamong', 'somerdale',
  'stratford', 'tabernacle', 'voorhees', 'washington-township',
  'west-deptford', 'willingboro', 'winslow-township', 'woodbury', 'woolwich',
]);

const CommunityPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { brandedAgent, isBranded } = useAgentBranding();
  const community = slug ? getCommunityBySlug(slug) : undefined;

  useEffect(() => {
    if (community) {
      document.title = isBranded && brandedAgent
        ? `${community.name} Homes for Sale — ${brandedAgent.display_name} | Prime Realty Partners`
        : (community.metaTitle || `${community.name}, NJ Homes for Sale (2026) | Prime Realty Partners`);
    }
  }, [community, isBranded, brandedAgent]);

  if (!community) {
    return (
      <Suspense fallback={null}>
        <NotFound />
      </Suspense>
    );
  }

  const canonicalUrl = `https://www.prpsj.com/${community.slug}`;

  // JSON-LD schemas
  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: `${community.name}, ${community.state}`,
    description: `Homes for sale in ${community.name}, NJ. Browse listings, neighborhoods, schools & market data.`,
    url: canonicalUrl,
    address: {
      "@type": "PostalAddress",
      addressLocality: community.name,
      addressRegion: community.state,
      addressCountry: "US",
    },
  };

  const agentSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateBrokerage",
    name: "Prime Realty Partners",
    url: "https://www.prpsj.com",
    telephone: "(856) 210-3000",
    address: {
      "@type": "PostalAddress",
      streetAddress: "31 Walnut Ave",
      addressLocality: "Bellmawr",
      addressRegion: "NJ",
      postalCode: "08031",
      addressCountry: "US",
    },
    areaServed: {
      "@type": "Place",
      name: `${community.name}, ${community.state}`,
    },
  };

  // FAQPage schema for rich results
  const faqSchema = community.faq && community.faq.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: community.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  const allSchemas: Record<string, unknown>[] = [placeSchema, agentSchema];
  if (faqSchema) allSchemas.push(faqSchema);

  const nearbyCommunities = community.nearbyCommunitySlugs
    .map((s) => getCommunityBySlug(s))
    .filter(Boolean);

  const ogImageSlug = community.slug.replace(/-homes-for-sale$/, '');
  const ogImage = TOWNS_WITH_OG_IMAGES.has(ogImageSlug)
    ? `https://www.prpsj.com/images/og/${ogImageSlug}.jpg`
    : 'https://www.prpsj.com/og-image.png';

  return (
    <Layout>
      <SEOHead
        title={isBranded && brandedAgent
          ? `${community.name} Homes for Sale — ${brandedAgent.display_name} | Prime Realty Partners`
          : (community.metaTitle || `Homes for Sale in ${community.name}, NJ`)}
        exactTitle={!(isBranded && brandedAgent)}
        description={isBranded && brandedAgent
          ? `${brandedAgent.display_name} is your ${community.name}, NJ real estate expert. Browse homes for sale, neighborhoods, schools & more.`
          : (community.metaDescription || `Homes for sale in ${community.name}, NJ. Browse current listings, explore neighborhoods, schools & local market data. Prime Realty Partners — your ${community.name} real estate experts.`)}
        canonicalUrl={canonicalUrl}
        keywords={community.keywords}
        ogImage={ogImage}
        ogType="website"
        schema={allSchemas}
        noindex={isBranded}
        geoPlacename={`${community.name}, ${community.state}`}
        breadcrumbs={[
          { name: "Home", url: "https://www.prpsj.com/" },
          { name: "Communities", url: "https://www.prpsj.com/communities" },
          { name: community.name, url: canonicalUrl },
        ]}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/15 via-primary/5 to-background py-16 md:py-24">
        <div className="container">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/communities" className="hover:text-primary transition-colors">Communities</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-foreground font-medium">{community.name}</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Homes for Sale in {community.name}, {community.state}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-6">
              {community.tagline}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-primary" />
                {community.county} County, {community.state}
              </span>
              {community.population && (
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-primary" />
                  Pop. {community.population}
                </span>
              )}
              {community.schoolDistrict && (
                <span className="flex items-center gap-1.5">
                  <GraduationCap className="h-4 w-4 text-primary" />
                  {community.schoolDistrict}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Agent Branding Card (when branded) */}
      {isBranded && brandedAgent && (
        <section className="py-8 md:py-10">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <AgentBrandingCard agent={brandedAgent} />
            </div>
          </div>
        </section>
      )}

      {/* Content Sections */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="max-w-4xl space-y-12">
            {/* Overview */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                About {community.name}
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                {community.overview}
              </p>
            </div>

            {/* Lifestyle */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Living in {community.name}
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                {community.lifestyle}
              </p>
            </div>

            {/* Real Estate */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                {community.name} Real Estate Market
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                {community.realEstate}
              </p>
            </div>

            {/* Houses for Sale H2 — uses "houses" phrasing for SEO keyword coverage */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Houses for Sale in {community.name}, NJ
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                Whether you're searching for houses for sale in {community.name} or exploring the broader {community.county} County market, Prime Realty Partners can help you find the right property. Our agents specialize in {community.name} real estate and know every neighborhood, school zone, and price range. Browse available homes for sale below or contact us for a personalized home search.
              </p>
            </div>

            {/* Zip Codes Section — Cherry Hill and others with zipCodeDescriptions */}
            {community.zipCodeDescriptions && community.zipCodeDescriptions.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  {community.name} Zip Codes
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg mb-6">
                  {community.name} is served by {community.zipCodeDescriptions.length} zip code{community.zipCodeDescriptions.length > 1 ? 's' : ''}, each with its own character and price range.
                </p>
                <div className="grid gap-4">
                  {community.zipCodeDescriptions.map((zd) => (
                    <Card key={zd.zip}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="rounded-lg bg-primary/10 p-3 shrink-0">
                            <MapPin className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-lg mb-1">
                              {community.name} {zd.zip}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {zd.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Neighborhoods Section */}
            {community.neighborhoods && (
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Neighborhoods in {community.name}
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {community.neighborhoods}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* IDX Listings Section */}
      {(community.idxSavedLinkId || community.idxWidgetId) && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container">
            {community.idxSavedLinkId ? (
              <IDXListingsGrid
                savedLinkId={community.idxSavedLinkId}
                communityName={community.name}
              />
            ) : community.idxWidgetId ? (
              <>
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                    Available Homes in {community.name}
                  </h2>
                  <p className="text-muted-foreground">
                    Live MLS listings updated frequently from Bright MLS via IDX Broker
                  </p>
                </div>
                <IDXListingsWidget widgetId={community.idxWidgetId} />
              </>
            ) : null}

            {community.idxSavedLinkUrl && (
              <div className="text-center mt-8">
                <Button size="lg" asChild>
                  <a
                    href={community.idxSavedLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gap-2"
                  >
                    Browse All {community.name} Listings
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Highlights Grid */}
      {community.highlights.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-center">
              Why {community.name}?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {community.highlights.map((highlight, index) => {
                const IconComponent = ICON_MAP[highlight.icon] || Home;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="rounded-lg bg-primary/10 p-3 shrink-0">
                          <IconComponent className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-lg mb-1">
                            {highlight.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {highlight.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Schools Section */}
      {community.schoolDistrict && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center">
              <GraduationCap className="h-10 w-10 text-primary mx-auto mb-4" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Schools in {community.name}
              </h2>
              <p className="text-lg text-muted-foreground mb-2">
                <span className="font-semibold text-foreground">{community.schoolDistrict}</span>
              </p>
              {community.schoolDescription && (
                <p className="text-muted-foreground leading-relaxed mt-4">
                  {community.schoolDescription}
                </p>
              )}
              <div className="mt-6">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="https://www.nj.gov/education/spr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gap-2"
                  >
                    View NJ School Performance Reports
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {community.faq && community.faq.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <HelpCircle className="h-10 w-10 text-primary mx-auto mb-4" />
                <h2 className="font-heading text-2xl md:text-3xl font-bold">
                  Frequently Asked Questions About {community.name}
                </h2>
              </div>
              <div className="space-y-4">
                {community.faq.map((item, index) => (
                  <details
                    key={index}
                    className="group border border-border rounded-lg"
                  >
                    <summary className="flex items-center justify-between cursor-pointer p-5 font-heading font-semibold text-foreground hover:text-primary transition-colors">
                      {item.question}
                      <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <div className="px-5 pb-5 text-muted-foreground leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Nearby Communities */}
      {nearbyCommunities.length > 0 && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-center">
              Explore Nearby Communities
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nearbyCommunities.map((nearby) =>
                nearby ? (
                  <Link
                    key={nearby.slug}
                    to={`/${nearby.slug}`}
                    className="group"
                  >
                    <Card className="hover:shadow-lg transition-shadow h-full">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-heading font-semibold text-lg group-hover:text-primary transition-colors">
                              {nearby.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {nearby.county} County • {nearby.tagline}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ) : null
              )}
            </div>
          </div>
        </section>
      )}

      {/* Helpful Resources */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="h-5 w-5 text-primary" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold">Helpful Resources</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/first-time-home-buyer-nj" className="group">
                <Card className="hover:shadow-lg transition-shadow h-full">
                  <CardContent className="pt-6">
                    <h3 className="font-heading font-semibold group-hover:text-primary transition-colors mb-2">First Time Buyer?</h3>
                    <p className="text-sm text-muted-foreground">Read our NJ First Time Home Buyer Guide</p>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-3" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/closing-costs-nj" className="group">
                <Card className="hover:shadow-lg transition-shadow h-full">
                  <CardContent className="pt-6">
                    <h3 className="font-heading font-semibold group-hover:text-primary transition-colors mb-2">Closing Costs</h3>
                    <p className="text-sm text-muted-foreground">Learn about Closing Costs in NJ</p>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-3" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/home-value" className="group">
                <Card className="hover:shadow-lg transition-shadow h-full">
                  <CardContent className="pt-6">
                    <h3 className="font-heading font-semibold group-hover:text-primary transition-colors mb-2">Home Value</h3>
                    <p className="text-sm text-muted-foreground">Find out what your home is worth</p>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-3" />
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Ready to Find Your Home in {community.name}?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {isBranded && brandedAgent
                ? `${brandedAgent.display_name || "Your agent"} knows ${community.name} inside and out. Reach out to get started.`
                : `Our agents know ${community.name} inside and out. Let us help you find the perfect property.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isBranded && brandedAgent?.lead_capture_url ? (
                <Button size="lg" asChild>
                  <a href={brandedAgent.lead_capture_url} target="_blank" rel="noopener noreferrer" className="gap-2">
                    <Phone className="h-4 w-4" />
                    Contact {brandedAgent.display_name?.split(" ")[0] || "Agent"}
                  </a>
                </Button>
              ) : (
                <Button size="lg" asChild>
                  <Link to={isBranded && brandedAgent?.slug ? `/agent/${brandedAgent.slug}` : "/contact"} className="gap-2">
                    <Phone className="h-4 w-4" />
                    {isBranded && brandedAgent ? `Contact ${brandedAgent.display_name?.split(" ")[0] || "Agent"}` : "Contact an Agent"}
                  </Link>
                </Button>
              )}
              {community.idxSavedLinkUrl && (
                <Button size="lg" variant="outline" asChild>
                  <a
                    href={community.idxSavedLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gap-2"
                  >
                    <Home className="h-4 w-4" />
                    Browse Listings
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MLS Disclaimer */}
      <section className="py-8 border-t border-border">
        <div className="container">
          <IDXDisclaimer variant="full" />
        </div>
      </section>
    </Layout>
  );
};

export default CommunityPage;
